'use client';

// Community-feedback poll card. Lives in the right sidebar of the feed,
// between the Activity Today stats and the Quick Actions list (see
// PostCategoryNavigation).
//
// Architecture note: the brief asked for a server-rendered shell with a
// client form child. The parent tree (/posts → InteractiveHomeFeed) is
// fully client, so this component fetches via /api/polls/active instead.
// Same data flow, same submit endpoint, same auth model — just an extra
// network hop on first paint. If the sidebar ever migrates to a server
// boundary, the data can be lifted into a parent server component.
//
// States:
//   - loading        → null (nothing renders; the slot is invisible until
//                      data arrives, which keeps layout stable)
//   - no active poll → null (slot collapses, no fallback per the brief)
//   - already submitted → thank-you state
//   - active + not submitted → scrollable form with sticky submit button

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PollQuestion {
  id: string;
  order_index: number;
  question_text: string;
  question_type: 'rating' | 'text';
  max_length: number | null;
}

interface ActivePoll {
  id: string;
  title: string;
  description: string | null;
  end_at: string;
  questions: PollQuestion[];
}

interface QuestionAverage {
  question_id: string;
  avg: number;
  n: number;
}

interface ActivePollResponse {
  poll: ActivePoll | null;
  hasSubmitted: boolean;
  isAuthenticated: boolean;
  // Present only when hasSubmitted is true (post-vote); rating questions only.
  averages?: QuestionAverage[];
}

// A question's results only surface once at least this many people have rated
// it — keeps a 2–3 vote average off the card (misleading at low n).
const RESULTS_MIN_VOTES = 5;

type AnswerMap = Record<
  string,
  { rating_value?: number; text_value?: string }
>;

export default function PollSidebarCard() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'inactive' }
    | { status: 'thanks'; poll: ActivePoll | null; averages: QuestionAverage[] }
    | { status: 'form'; poll: ActivePoll }
  >({ status: 'loading' });

  const [answers, setAnswers] = useState<AnswerMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/polls/active', { cache: 'no-store' });
        if (!res.ok) {
          if (!cancelled) setState({ status: 'inactive' });
          return;
        }
        const data = (await res.json()) as ActivePollResponse;
        if (cancelled) return;
        if (!data.poll || !data.isAuthenticated) {
          setState({ status: 'inactive' });
          return;
        }
        if (data.hasSubmitted) {
          setState({ status: 'thanks', poll: data.poll, averages: data.averages ?? [] });
          return;
        }
        setState({ status: 'form', poll: data.poll });
      } catch {
        if (!cancelled) setState({ status: 'inactive' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateRating = useCallback((questionId: string, value: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...(prev[questionId] || {}), rating_value: value },
    }));
  }, []);

  const updateText = useCallback((questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...(prev[questionId] || {}), text_value: value },
    }));
  }, []);

  const submit = useCallback(async () => {
    if (state.status !== 'form') return;
    const poll = state.poll;
    setSubmitting(true);
    setSubmitError(null);

    // Re-fetch the active poll so the thank-you can show running averages —
    // now that hasSubmitted is true, /api/polls/active returns them. Best
    // effort: the thank-you still renders if this fails.
    const toThanks = async () => {
      let averages: QuestionAverage[] = [];
      try {
        const res = await fetch('/api/polls/active', { cache: 'no-store' });
        if (res.ok) {
          const data = (await res.json()) as ActivePollResponse;
          averages = data.averages ?? [];
        }
      } catch {
        /* keep averages empty */
      }
      setState({ status: 'thanks', poll, averages });
    };

    try {
      const payload = {
        answers: Object.entries(answers).map(([question_id, val]) => ({
          question_id,
          rating_value: val.rating_value ?? null,
          text_value: val.text_value ?? null,
        })),
      };
      const res = await fetch(`/api/polls/${poll.id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 409) {
        await toThanks();
        return;
      }
      if (!res.ok && res.status !== 207) {
        const body = await res.json().catch(() => ({}));
        setSubmitError(body?.error || 'Failed to submit. Please try again.');
        return;
      }
      await toThanks();
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [state, answers]);

  if (state.status === 'loading' || state.status === 'inactive') {
    return null;
  }

  if (state.status === 'thanks') {
    const ratingQuestions = (state.poll?.questions ?? []).filter(
      (q) => q.question_type === 'rating',
    );
    const avgById = new Map(state.averages.map((a) => [a.question_id, a]));
    // Only show a question's result once enough people have rated it.
    const shown = ratingQuestions
      .map((q) => ({ q, a: avgById.get(q.id) }))
      .filter(
        (x): x is { q: PollQuestion; a: QuestionAverage } =>
          !!x.a && x.a.n >= RESULTS_MIN_VOTES,
      );
    const anyBelowThreshold = ratingQuestions.some((q) => {
      const a = avgById.get(q.id);
      return !a || a.n < RESULTS_MIN_VOTES;
    });

    return (
      <div className="bg-surface-2 border border-border-subtle rounded-sm p-4">
        <div className="flex items-start gap-2">
          <div className="h-2 w-2 mt-1.5 rounded-full bg-accent flex-shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-text-primary">Thanks for your feedback</h3>
            {shown.length === 0 ? (
              // Nothing has cleared the threshold — one quiet line, no per-question noise.
              <p className="text-xs text-text-secondary mt-1">
                Results appear once enough votes are in.
              </p>
            ) : (
              <p className="text-xs text-text-secondary mt-1">Here&apos;s how the community feels so far.</p>
            )}
          </div>
        </div>

        {shown.length > 0 && (
          <ul className="mt-3 space-y-2.5">
            {shown.map(({ q, a }) => (
              <li key={q.id} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs text-text-secondary leading-snug flex-1 min-w-0">
                    {q.question_text}
                  </span>
                  <span className="text-xs font-semibold text-text-primary tabular-nums flex-shrink-0">
                    {a.avg.toFixed(1)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden"
                    role="img"
                    aria-label={`Average ${a.avg.toFixed(1)} out of 5 from ${a.n} votes`}
                  >
                    <div
                      className="h-full bg-accent"
                      style={{ width: `${(a.avg / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-text-tertiary tabular-nums flex-shrink-0 w-12 text-right">
                    {a.n} {a.n === 1 ? 'vote' : 'votes'}
                  </span>
                </div>
              </li>
            ))}
            {anyBelowThreshold && (
              // Some questions haven't reached the threshold — one shared line, not per-question.
              <li className="text-[11px] text-text-tertiary pt-0.5">
                More results appear once enough votes are in.
              </li>
            )}
          </ul>
        )}
      </div>
    );
  }

  const { poll } = state;
  return (
    <div className="bg-surface-2 border border-border-subtle rounded-sm flex flex-col max-h-[520px]">
      <div className="px-4 pt-4 pb-3 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-text-primary leading-snug">{poll.title}</h3>
        {poll.description && (
          <p className="text-xs text-text-secondary mt-1 leading-relaxed">{poll.description}</p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {poll.questions.map((q) => (
          <PollQuestionRow
            key={q.id}
            question={q}
            answer={answers[q.id]}
            onRatingChange={(v) => updateRating(q.id, v)}
            onTextChange={(v) => updateText(q.id, v)}
          />
        ))}
      </div>

      <div className="border-t border-border-subtle px-4 py-3 bg-surface-2">
        {submitError && (
          <p className="text-xs text-status-busy mb-2">{submitError}</p>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="w-full inline-flex items-center justify-center h-8 px-3 rounded-sm bg-accent text-text-on-accent text-sm font-medium hover:bg-accent-hover active:bg-accent-active transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
        <p className="text-[11px] text-text-tertiary mt-1.5 text-center">
          Skipping is fine — submit any subset.
        </p>
      </div>
    </div>
  );
}

interface PollQuestionRowProps {
  question: PollQuestion;
  answer?: { rating_value?: number; text_value?: string };
  onRatingChange: (value: number) => void;
  onTextChange: (value: string) => void;
}

function PollQuestionRow({
  question,
  answer,
  onRatingChange,
  onTextChange,
}: PollQuestionRowProps) {
  if (question.question_type === 'rating') {
    const selected = answer?.rating_value ?? 0;
    return (
      <div>
        <p className="text-xs text-text-primary leading-snug mb-2">{question.question_text}</p>
        <RatingStars value={selected} onChange={onRatingChange} />
      </div>
    );
  }

  const maxLen = question.max_length ?? 200;
  const currentText = answer?.text_value ?? '';
  return (
    <div>
      <p className="text-xs text-text-primary leading-snug mb-2">{question.question_text}</p>
      <textarea
        value={currentText}
        onChange={(e) => onTextChange(e.target.value.slice(0, maxLen))}
        maxLength={maxLen}
        rows={3}
        className="w-full bg-surface-3 border border-border-subtle rounded-sm px-2 py-1.5 text-text-primary text-xs placeholder:text-text-tertiary focus:border-accent focus:outline-none transition-colors resize-y min-h-[60px]"
        placeholder="Optional"
      />
      <p className="text-[11px] text-text-tertiary mt-1 text-right tabular-nums">
        {currentText.length} / {maxLen}
      </p>
    </div>
  );
}

interface RatingStarsProps {
  value: number;
  onChange: (value: number) => void;
}

function RatingStars({ value, onChange }: RatingStarsProps) {
  // Five-star control with clear hover + selected state. Matches the
  // existing icon weight (strokeWidth 1.75) used elsewhere in the feed.
  const labels = useMemo(() => ['1', '2', '3', '4', '5'], []);
  return (
    <div className="flex items-center gap-1">
      {labels.map((label, idx) => {
        const star = idx + 1;
        const active = star <= value;
        return (
          <button
            key={label}
            type="button"
            onClick={() => onChange(star)}
            aria-label={`${star} of 5`}
            className={cn(
              'p-1 rounded-sm transition-colors',
              active ? 'text-accent' : 'text-text-tertiary hover:text-text-secondary'
            )}
          >
            <Star
              className="h-4 w-4"
              strokeWidth={1.75}
              fill={active ? 'currentColor' : 'none'}
            />
          </button>
        );
      })}
    </div>
  );
}
